/**
 * ThemeContext.js - Contexto para compartir el estado del tema entre componentes
 *
 * Soporta 3 modos: light (default), regular (marca Soquimat) y dark.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import {
  THEME_MODES,
  normalizeThemeMode,
  setActiveThemeMode,
  getTheme,
  getUIColors,
} from '../components/layout/theme';

export const ThemeContext = createContext(null);

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext debe ser usado dentro de un ThemeProvider');
  }
  return context;
};

const readStoredThemeMode = () => {
  const saved = localStorage.getItem('theme');
  if (THEME_MODES.includes(saved)) return saved;
  return 'light';
};

export const ThemeProvider = ({ children }) => {
  const [colorMode, setColorModeState] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    const mode = readStoredThemeMode();
    setActiveThemeMode(mode);
    return mode;
  });

  useEffect(() => {
    setActiveThemeMode(colorMode);
    localStorage.setItem('theme', colorMode);
  }, [colorMode]);

  const setColorMode = useCallback((mode) => {
    setColorModeState(normalizeThemeMode(mode));
  }, []);

  const cycleTheme = useCallback(() => {
    setColorModeState((prev) => {
      const idx = THEME_MODES.indexOf(prev);
      return THEME_MODES[(idx + 1) % THEME_MODES.length];
    });
  }, []);

  /** @deprecated Prefer setColorMode / cycleTheme. Kept for compatibility. */
  const toggleTheme = useCallback(() => {
    cycleTheme();
  }, [cycleTheme]);

  const isDarkMode = colorMode === 'dark';
  const isRegularMode = colorMode === 'regular';
  const theme = useMemo(() => getTheme(colorMode), [colorMode]);
  const ui = useMemo(() => getUIColors(theme), [theme]);

  const value = {
    colorMode,
    setColorMode,
    cycleTheme,
    toggleTheme,
    isDarkMode,
    isRegularMode,
    theme,
    ui,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
